import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Button,
  Checkbox,
  Content,
  Form,
  FormGroup,
  FormGroupLabelHelp,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  Spinner,
  TextInput,
  Tooltip,
} from '@patternfly/react-core';
import { MagicIcon } from '@patternfly/react-icons';
import InTransferAlert from '../Domains/InTransferAlert';
import { useApiFetch } from '../../../api';
import { usePrices } from '../../../hooks/usePrices';
import { formatDateNumeric } from '../../../utils/dateFormat';
import NotificationRecipientsField from './NotificationRecipientsField';
import { classifyMailboxBilling, proRataQuantity } from './mailboxProRata';

interface MailboxCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mailDomainId: number;
  domainName: string;
  /** The mail domain's yearly mail-billing anchor (next_billing_at); null = mail billing inactive. */
  nextBillingAt: string | null;
  /** NetBox IMAP server id — non-null = dedicated binding (not billed per mailbox). */
  imapServerId: number | null;
  /** True when the backing domain is mid-transfer — mailboxes prepared now are provisional. */
  domainInTransfer?: boolean;
  /** The backing domain's transfer deadline (ISO string) or null. */
  transferDeadline?: string | null;
  onSuccess: () => void;
}

const LOCAL_PART_REGEX = /^[a-zA-Z0-9._-]+$/;

const PRICE_FMT = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

// Strong-password generator for the "Suggest a strong password" action. Crypto-grade randomness,
// excludes visually ambiguous characters (the password is shown and copied, so readability matters),
// and guarantees at least one lowercase, uppercase, digit, and symbol.
const PW_LENGTH = 20;
const PW_LOWER = 'abcdefghijkmnpqrstuvwxyz'; // no l
const PW_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I, O
const PW_DIGITS = '23456789'; // no 0, 1
const PW_SYMBOLS = '!@#$%*-_=+?';

function randomIndex(maxExclusive: number): number {
  // Unbiased: reject the byte values in the wrap-around tail so every index is equally likely.
  const limit = 256 - (256 % maxExclusive);
  const buf = new Uint8Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= limit);
  return value % maxExclusive;
}

function pickFrom(chars: string): string {
  return chars.charAt(randomIndex(chars.length));
}

function generateStrongPassword(): string {
  const all = PW_LOWER + PW_UPPER + PW_DIGITS + PW_SYMBOLS;
  const chars = [pickFrom(PW_LOWER), pickFrom(PW_UPPER), pickFrom(PW_DIGITS), pickFrom(PW_SYMBOLS)];
  while (chars.length < PW_LENGTH) {
    chars.push(pickFrom(all));
  }
  // Fisher–Yates shuffle so the four guaranteed characters aren't pinned to the front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function MailboxCreateModal({
  isOpen,
  onClose,
  mailDomainId,
  domainName,
  nextBillingAt,
  imapServerId,
  domainInTransfer,
  transferDeadline,
  onSuccess,
}: MailboxCreateModalProps) {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [mailboxName, setMailboxName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyRecipients, setNotifyRecipients] = useState<string[]>(() => {
    const operatorEmail = auth.user?.profile?.email;
    return typeof operatorEmail === 'string' && operatorEmail.length > 0 ? [operatorEmail] : [];
  });
  const [notifyBufferHasInput, setNotifyBufferHasInput] = useState(false);

  const isLocalPartValid = LOCAL_PART_REGEX.test(mailboxName);
  const isPasswordValid = password.length >= 8;
  const isConfirmValid = confirmPassword.length > 0 && confirmPassword === password;
  const isNotifyValid = !notifyEnabled || (notifyRecipients.length > 0 && !notifyBufferHasInput);
  const isFormValid = isLocalPartValid && isPasswordValid && isConfirmValid && isNotifyValid;

  // Pro-rata billing estimate. The MAILBOX yearly price is only needed (and only fetched) when the
  // domain is actually billed per mailbox and the modal is open.
  const billing = classifyMailboxBilling(nextBillingAt, imapServerId);
  const { prices, loading: priceLoading, error: priceError } = usePrices(isOpen && billing.kind === 'prorata');
  const mailboxPrice = prices.find((p) => p.product_key === 'MAILBOX');

  const handleSuggestPassword = () => {
    const generated = generateStrongPassword();
    setPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true); // reveal so the operator can review and copy the suggestion
  };

  const resetAndClose = () => {
    setMailboxName('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(true);
    setError(null);
    setNotifyEnabled(true);
    const operatorEmail = auth.user?.profile?.email;
    setNotifyRecipients(typeof operatorEmail === 'string' && operatorEmail.length > 0 ? [operatorEmail] : []);
    setNotifyBufferHasInput(false);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        mail_domain_id: mailDomainId,
        user: mailboxName,
        password,
      };
      if (notifyEnabled && notifyRecipients.length > 0) {
        body.notification_emails = notifyRecipients;
      }

      const response = await apiFetch('/emails/mailboxes', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error((body as { message?: string })?.message ?? `HTTP error! status: ${response.status}`);
      }

      resetAndClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create mailbox');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Info card shown just above the Create button: explains how this mailbox is billed.
  const renderBillingInfo = () => {
    if (billing.kind === 'not-billed') {
      return (
        <Alert variant="info" isInline title="No per-mailbox billing">
          This mailbox isn&apos;t billed individually — mailboxes on a dedicated mail server are covered by that
          server&apos;s billing.
        </Alert>
      );
    }

    if (billing.kind === 'deferred') {
      return (
        <Alert variant="info" isInline title="Billed with the domain">
          This mailbox will be included in {domainName}&apos;s next yearly mail billing.
        </Alert>
      );
    }

    // prorata — needs the yearly MAILBOX price.
    if (priceLoading) {
      return (
        <Alert variant="info" isInline title="Calculating billing">
          <Spinner size="sm" aria-label="Calculating the pro-rata charge" /> Estimating the pro-rata charge…
        </Alert>
      );
    }

    if (priceError || !mailboxPrice || mailboxPrice.recurring !== 'yearly') {
      return (
        <Alert variant="info" isInline title="Pro-rata billing">
          This mailbox is billed pro-rata until {domainName}&apos;s renewal on {formatDateNumeric(billing.anchor)}.
          Pricing details are currently unavailable.
        </Alert>
      );
    }

    const yearly = parseFloat(mailboxPrice.unit_price);
    const amount = proRataQuantity(billing.startedMonths) * yearly;
    const monthLabel = billing.startedMonths === 1 ? 'month' : 'months';

    return (
      <Alert variant="info" isInline title="Pro-rata billing">
        Creating this mailbox adds{' '}
        <strong>{Number.isNaN(amount) ? mailboxPrice.unit_price : PRICE_FMT.format(amount)} excl. VAT</strong> to your
        next invoice — {billing.startedMonths} {monthLabel} until {domainName}&apos;s renewal on{' '}
        {formatDateNumeric(billing.anchor)}, prorated from {PRICE_FMT.format(yearly)}/year.
      </Alert>
    );
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={resetAndClose} aria-label="Create mailbox">
      <ModalHeader title="Create mailbox / email address" />
      <ModalBody>
        {domainInTransfer && (
          <InTransferAlert variant="mail" deadline={transferDeadline ?? null} className="pf-v6-u-mb-md">
            <Content component="p" className="pf-v6-u-mt-sm">
              Mailboxes you create now are free until the transfer completes, and are removed if it fails.
            </Content>
          </InTransferAlert>
        )}
        {error && (
          <Alert variant="danger" isInline title="Creation failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Form>
          <FormGroup
            label="Mailbox name"
            isRequired
            fieldId="create-mailbox-user"
            labelHelp={
              <Popover
                headerContent="Mailbox name"
                bodyContent={
                  <>
                    The email address is generated automatically by combining this name with the mail domain. For
                    example, entering <code>info</code> on the <code>{domainName}</code> domain creates the address{' '}
                    <code>info@{domainName}</code>.
                  </>
                }
              >
                <FormGroupLabelHelp aria-label="Mailbox name help" />
              </Popover>
            }
          >
            <TextInput
              id="create-mailbox-user"
              value={mailboxName}
              onChange={(_event, value) => setMailboxName(value)}
              validated={mailboxName && !isLocalPartValid ? 'error' : 'default'}
            />
            {mailboxName && !isLocalPartValid ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">
                    Only letters, numbers, dots, hyphens, and underscores are allowed.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              mailboxName.trim().length > 0 && (
                <Content component="small" className="pf-v6-u-color-200 pf-v6-u-mt-xs">
                  Email address:{' '}
                  <strong>
                    {mailboxName.trim()}@{domainName}
                  </strong>
                </Content>
              )
            )}
          </FormGroup>
          <FormGroup
            label="Password"
            isRequired
            fieldId="create-mailbox-password"
            labelHelp={
              <Tooltip content="Suggest a strong password">
                <Button
                  variant="plain"
                  aria-label="Suggest a strong password"
                  icon={<MagicIcon />}
                  onClick={handleSuggestPassword}
                />
              </Tooltip>
            }
          >
            <TextInput
              id="create-mailbox-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(_event, value) => setPassword(value)}
              validated={password && !isPasswordValid ? 'error' : 'default'}
            />
            {password && !isPasswordValid && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">Password must be at least 8 characters.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <FormGroup label="Confirm password" isRequired fieldId="create-mailbox-confirm">
            <TextInput
              id="create-mailbox-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(_event, value) => setConfirmPassword(value)}
              validated={confirmPassword && !isConfirmValid ? 'error' : 'default'}
            />
            {confirmPassword && !isConfirmValid && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">Passwords do not match.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <Checkbox
            id="create-mailbox-show-password"
            label="Show password"
            isChecked={showPassword}
            onChange={(_event, checked) => setShowPassword(checked)}
          />
          <NotificationRecipientsField
            enabled={notifyEnabled}
            onEnabledChange={setNotifyEnabled}
            recipients={notifyRecipients}
            onRecipientsChange={setNotifyRecipients}
            hasUnchipifiedInput={notifyBufferHasInput}
            onHasUnchipifiedInputChange={setNotifyBufferHasInput}
          />
        </Form>
        {/* While the backing domain is mid-transfer the mail domain is unbilled (next_billing_at is
            null until finalization), so classifyMailboxBilling would show the generic "not billed"
            copy — which reads as a dedicated-server reason and contradicts the transfer alert above.
            The InTransferAlert's "free until the transfer completes" line is the accurate billing
            explanation in that window, so suppress the billing card. */}
        {!domainInTransfer && <div className="pf-v6-u-mt-md">{renderBillingInfo()}</div>}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isDisabled={!isFormValid || isSubmitting}
          isLoading={isSubmitting}
        >
          Create
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailboxCreateModal;
